import { API } from "apis";
import RowsPerPageSelect from "components/common/RowsPerPageSelect";
import Image from "components/common/images/Image";
import PageLabel from "components/common/paging/PageLabel";
import Pagination from "components/common/paging/Pagination2";
import ItemListMainCategory from "components/item/list/ItemListMainCategory";
import ItemListSearchOptionsArea from "components/item/list/ItemListSearchOptionsArea";
import ItemListSearchSortSelect from "components/item/list/ItemListSearchSortSelect";
import ItemListSideMenu from "components/item/list/ItemListSideMenu";
import { CategoryType, PER_PAGE_TYPES, SaleStatusType } from "enums";
import { CategoryModel, ContextModel, ItemSearchOptions, PublicItemListModel, PublicItemModel } from "models";
import qs from "query-string";
import React from "react";
import { Helmet } from "react-helmet-async";
import { FaRegHeart } from "react-icons/fa";
import { Link, useLocation, useNavigate, useOutletContext } from "react-router-dom";
import { Utils } from "utils";

/** 「商品一覧」画面 */
const ItemListPage: React.FC = () => {
  const ctx = useOutletContext<ContextModel>();

  const location = useLocation();
  const navigate = useNavigate();

  /** カテゴリの全情報 */
  const [categoryList, setCategoryList] = React.useState<CategoryModel[]>([]);

  // 直前に検索を行ったクエリ
  const searchedQuery = React.useRef<string>();

  const options = React.useRef<ItemSearchOptions>({});
  const [data, setData] = React.useState<PublicItemListModel>();

  const reload = async () => {
    for (let key in options.current) {
      if ((options.current as any)[key] === "") {
        delete (options.current as any)[key];
      }
    }
    const params = qs.stringify(options.current);
    navigate({ pathname: location.pathname, search: params });
  };

  const onChangeLargeCategory = async (categoryId: string) => {
    const cat = categoryList.find((v) => v.id === categoryId);
    if (!cat) {
      return;
    }

    options.current.item_large_category_id = cat.id;
    delete options.current.item_medium_category_id;
    delete options.current.item_small_category_id;
    delete options.current.keyword;
    delete options.current.page_no;
    delete options.current.sort_order;
    options.current.get_count = PER_PAGE_TYPES.DEFAULT;

    reload();
  };

  const onChangeCategory = async (target: CategoryModel) => {
    if (target.type === CategoryType.LARGE) {
      delete options.current.item_medium_category_id;
      delete options.current.item_small_category_id;
      delete options.current.page_no;
      delete options.current.sort_order;
      options.current.get_count = PER_PAGE_TYPES.DEFAULT;
    }
    if (target.type === CategoryType.MIDDLE) {
      options.current.item_medium_category_id = target.id;
      delete options.current.item_small_category_id;
      delete options.current.page_no;
      delete options.current.sort_order;
      options.current.get_count = PER_PAGE_TYPES.DEFAULT;
    }
    if (target.type === CategoryType.SMALL) {
      const lg = categoryList.find((v) => v.id === options.current.item_large_category_id);
      if (!lg) {
        return;
      }
      for (const md of lg.list) {
        if (md.list.find((v) => v.id === target.id)) {
          options.current.item_medium_category_id = md.id;
          options.current.item_small_category_id = target.id;
          delete options.current.page_no;
          delete options.current.sort_order;
          options.current.get_count = PER_PAGE_TYPES.DEFAULT;
          break;
        }
      }
    }
    reload();
  };

  const onSearch = async () => {
    delete options.current.page_no;
    delete options.current.sort_order;
    delete options.current.corp_id;
    options.current.get_count = PER_PAGE_TYPES.DEFAULT;
    reload();
  };

  const onChangeSort = async () => {
    delete options.current.page_no;

    reload();
  };

  const onChangePage = async (value: number) => {
    if (value <= 1) {
      delete options.current.page_no;
    } else {
      options.current.page_no = value;
    }

    reload();
  };

  const onAddCandidate = async (item: PublicItemModel, index: number) => {
    console.log("商談候補に追加", item.item_name);

    ctx.loading.setState(true);
    try {
      // check item has candidate
      if (item.candidate_item_cd) {
        await API.CandidateItem.del(item.item_cd);
      } else {
        await API.CandidateItem.add(item.item_cd, 1);
      }

      let newDataItem = [...data?.public_item as PublicItemModel[]];
      newDataItem[index] = {
        ...newDataItem[index],
        candidate_item_cd: item.item_cd === item.candidate_item_cd ? null : item.item_cd
      } as any;

      setData({ ...data, public_item: newDataItem } as PublicItemListModel);
    } catch (err) {
      Utils.errorAction(ctx, err);
    }
    ctx.loading.setState(false);
  };

  const onAddFavorite = async (item: PublicItemModel, index: number) => {
    console.log("お気に入りに追加", item.item_name);

    ctx.loading.setState(true);
    try {
      // check item has favorite
      if (item.favorite_item_cd) {
        await API.FavoriteItem.del(item.item_cd);
      } else {
        await API.FavoriteItem.add(item.item_cd);
      }

      let newDataItem = [...data?.public_item as PublicItemModel[]];
      newDataItem[index] = {
        ...newDataItem[index],
        favorite_item_cd: item.item_cd === item.favorite_item_cd ? null : item.item_cd
      } as any;

      setData({ ...data, public_item: newDataItem } as PublicItemListModel);
    } catch (err) {
      Utils.errorAction(ctx, err);
    }

    ctx.loading.setState(false);
  };

  React.useEffect(() => {
    if (searchedQuery.current === location.search) {
      console.log("useEffect => load cancel.");
      return;
    }
    console.log("useEffect => load start. : " + location.search);
    searchedQuery.current = location.search;
    const f = async () => {
      ctx.loading.setState(true);
      if (!ctx.auth.data) {
        await ctx.auth.initialize();
      }

      // optionsを一度クリアしてから、クエリの状態に
      for (let k in options.current) {
        delete (options.current as any)[k];
      }
      const query = qs.parse(location.search);
      if (query.page_no !== undefined) {
        options.current.page_no = Number(query.page_no);
      }
      if (query.sort_order !== undefined) {
        options.current.sort_order = String(query.sort_order);
      }
      if (query.item_large_category_id !== undefined) {
        options.current.item_large_category_id = String(query.item_large_category_id);
      }
      if (query.item_medium_category_id !== undefined) {
        options.current.item_medium_category_id = String(query.item_medium_category_id);
      }
      if (query.item_small_category_id !== undefined) {
        options.current.item_small_category_id = String(query.item_small_category_id);
      }
      if (query.keyword !== undefined) {
        options.current.keyword = String(query.keyword);
      }
      if (query.price_from !== undefined) {
        options.current.price_from = Number(query.price_from);
      }
      if (query.price_to !== undefined) {
        options.current.price_to = Number(query.price_to);
      }
      if (query.get_count !== undefined) {
        options.current.get_count = Number(query.get_count);
      }
      if (query.corp_id !== undefined) {
        options.current.corp_id = String(query.corp_id);
      }

      try {
        if (categoryList.length === 0) {
          // 商品カテゴリ未ロードならロード
          const response = await API.CategoryItem.list();
          console.log(response);
          response.data.unshift({
            name: "すべて",
            id: "",
            type: CategoryType.LARGE,
            list: [],
            sort_no: -1,
          });
          setCategoryList(response.data);
        }
      } catch (err) {
        Utils.errorAction(ctx, err);
      }
      const response = await API.PublicItem.search(options.current);
      console.log(response);
      setData(response.data);
      window.scrollTo({
        top: 0,
        behavior: "auto",
      });

      ctx.loading.setState(false);
    };
    f();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  if (!data) {
    return <div></div>;
  }

  return (
    <div className={`flex flex-col h-full`}>
      <Helmet>
        <title>商品一覧</title>
      </Helmet>
      <ItemListMainCategory value={options.current.item_large_category_id} data={categoryList} onChange={onChangeLargeCategory} />
      <div className="flex border-t flex-1">
        <ItemListSideMenu
          options={options.current}
          category={categoryList.find((v) => v.id === options.current.item_large_category_id)}
          onSelect={onChangeCategory}
        />
        <div className="flex-1">
          {/* 検索条件 */}
          <ItemListSearchOptionsArea options={options.current} onSearch={onSearch} />

          {/* 検索結果 */}
          {data.public_item.length === 0 ? (
            <div className="flex justify-center py-12">
              <div>検索結果がありません。</div>
            </div>
          ) : (
            <>
              <div className="p-4">
                <div className="flex gap-10 mb-2">
                  <div>
                    <RowsPerPageSelect options={options.current} onChange={onChangeSort} />
                    <span>を表示</span>
                  </div>
                  <div>
                    <span className="mr-4">並び替え</span>
                    <ItemListSearchSortSelect options={options.current} onChange={onChangeSort} />
                  </div>
                </div>
                <PageLabel data={data} />
                <div className="flex flex-wrap gap-4">
                  {data.public_item.map((item, index) => (
                    <div key={index} className="p-2 item-hover w-[250px]">
                      <div>
                        <Link to={`/item-detail/${item.item_cd}/`}>
                          <Image data={item.item_main_image} height="250px" default="item.png" />
                        </Link>
                        <Link to={`/item-detail/${item.item_cd}/`} className="hover:underline">
                          <div className="font-bold my-2 whitespace-nowrap overflow-hidden text-ellipsis" title={item.item_name}>
                            {item.item_name}
                          </div>
                        </Link>
                      </div>

                      <div className="font-normal text-2xl text-jhk-blue">
                        {item.is_discretionary_price ? (
                          <>-</>
                        ) : (
                          <>
                            {Utils.addComma(item.sale_price)}
                            <span className="text-sm">円(税抜)</span>
                          </>
                        )}
                      </div>
                      <Link to={`/company-detail/${item.sale_corp_id}/`}>
                        <div className="text-gray-400 hover:underline truncate line-1">{item.sale_corp_name}</div>
                      </Link>
                      {ctx.auth.data?.is_admin_corp === true ? (
                        <>
                          <div>{item.item_cd}</div>
                          {item.sale_status === SaleStatusType._2_TEISHI && <div>販売停止</div>}
                          {item.list_type === 1 && <div>限定公開設定あり</div>}
                          {item.list_type === 2 && <div>非公開設定あり</div>}
                        </>
                      ) : (
                        <>
                          <button
                            className={`w-full border-2 border-black font-bold my-2 add-item ${item.item_cd == item.candidate_item_cd ? 'item-disable': ''}`}
                            onClick={() => {
                              onAddCandidate(item, index);
                            }}
                          >
                            {item.item_cd == item.candidate_item_cd ? '商談候補商品に追加済' : '商談候補商品に追加'}
                          </button>
                          <button
                            className={`w-full border border-jhk-blue rounded flex justify-center items-center text-jhk-blue gap-2 item-favorite ${item.item_cd == item.favorite_item_cd ? 'is-favorite' : ''}`}
                            onClick={() => {
                              onAddFavorite(item, index);
                            }}
                          >
                            <FaRegHeart />
                            <span>{item.item_cd == item.favorite_item_cd ? 'お気に入り済' : 'お気に入り'}</span>
                          </button>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              </div>
              <div className="flex justify-center m-4">
                <Pagination page={options.current?.page_no ?? 1} data={data} onChange={onChangePage} />
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
export default ItemListPage;
